import { useMemo, useState } from 'react';
import { X, Upload, Check, AlertCircle } from 'lucide-react';
import { useCards, type Card } from '@/hooks/useCards';
import { useSetDeckCardQty } from '@/hooks/useDecks';
import { InkDot } from '@/components/InkDot';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

interface DeckImportSheetProps {
  open: boolean;
  onClose: () => void;
  deckId: string;
  allowedInks: [InkColor, InkColor];
}

interface ParsedLine {
  raw: string;
  qty: number;
  name: string;
  subtitle?: string;
}

interface MatchResult extends ParsedLine {
  card: Card | null;
  reason?: string;
}

const PLACEHOLDER = `Paste a deck list, one card per line:

4 Mickey Mouse - Brave Little Tailor
3 Be Prepared
2 Tipo - Growing Son
…`;

/** Parse "<qty> <Name>[ - <Subtitle>]" lines. Ignores blanks, comments, headers. */
function parseDeckText(text: string): ParsedLine[] {
  const lines = text.split(/\r?\n/);
  const out: ParsedLine[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('#') || line.startsWith('//')) continue;
    const m = line.match(/^(\d+)\s*[xX]?\s+(.+)$/);
    if (!m) continue;
    const qty = Math.max(1, Math.min(4, parseInt(m[1], 10)));
    let rest = m[2].trim();
    // Strip trailing "(SET 123)" style tokens.
    rest = rest.replace(/\s*\([^)]*\)\s*$/, '').trim();
    let name = rest;
    let subtitle: string | undefined;
    const dashIdx = rest.search(/\s[-–—]\s/);
    if (dashIdx > 0) {
      name = rest.slice(0, dashIdx).trim();
      subtitle = rest.slice(dashIdx + 3).trim();
    }
    out.push({ raw: line, qty, name, subtitle });
  }
  return out;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchLines(parsed: ParsedLine[], cards: Card[], allowedInks: InkColor[]): MatchResult[] {
  const byName = new Map<string, Card[]>();
  for (const c of cards) {
    const key = normalize(c.name);
    const arr = byName.get(key) || [];
    arr.push(c);
    byName.set(key, arr);
  }
  return parsed.map(p => {
    const key = normalize(p.name);
    let candidates = byName.get(key) || [];
    if (!candidates.length) {
      return { ...p, card: null, reason: 'Not found' };
    }
    if (p.subtitle) {
      const sub = normalize(p.subtitle);
      const sm = candidates.filter(c => c.subtitle && normalize(c.subtitle) === sub);
      if (sm.length) candidates = sm;
    }
    const inkMatch = candidates.filter(c => allowedInks.includes(c.ink_color as InkColor));
    const final = inkMatch[0] || candidates[0];
    const reason = inkMatch.length ? undefined : `Ink ${final.ink_color} not in deck`;
    return { ...p, card: final, reason };
  });
}

export function DeckImportSheet({ open, onClose, deckId, allowedInks }: DeckImportSheetProps) {
  const [text, setText] = useState('');
  const [importing, setImporting] = useState(false);
  const { data: cards } = useCards();
  const setQty = useSetDeckCardQty();

  const parsed = useMemo(() => parseDeckText(text), [text]);
  const matched = useMemo(
    () => (cards ? matchLines(parsed, cards, allowedInks) : []),
    [parsed, cards, allowedInks],
  );

  const goodCount = matched.filter(m => m.card && !m.reason).length;
  const totalQty = matched.reduce((s, m) => (m.card && !m.reason ? s + m.qty : s), 0);

  if (!open) return null;

  const handleImport = async () => {
    if (!cards) return;
    setImporting(true);
    let added = 0;
    let skipped = 0;
    for (const m of matched) {
      if (!m.card || m.reason) {
        skipped++;
        continue;
      }
      try {
        await setQty.mutateAsync({ deckId, cardId: m.card.id, quantity: m.qty });
        added++;
      } catch {
        skipped++;
      }
    }
    setImporting(false);
    toast.success(`Imported ${added} card${added === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped` : ''}`);
    onClose();
    setText('');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[90vh] flex flex-col rounded-t-2xl border border-border bg-card" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold text-foreground">Import Deck List</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-[11px] text-muted-foreground font-body">
            Pastes from Dreamborn, Inkdecks, or plain text. Existing quantities will be replaced.
          </p>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={10}
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-xs text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />

          {parsed.length > 0 && (
            <div className="rounded-lg border border-border bg-secondary/50 p-3 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-body">
                <span className="text-foreground font-medium">{goodCount}/{parsed.length} lines matched</span>
                <span className="text-muted-foreground">{totalQty} cards · {[...allowedInks].join(' / ')}</span>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-0.5 mt-1">
                {matched.map((m, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] font-body">
                    {m.card && !m.reason ? (
                      <Check className="h-3 w-3 text-ink-emerald flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-3 w-3 text-ink-ruby flex-shrink-0" />
                    )}
                    <span className="text-muted-foreground w-4 flex-shrink-0">{m.qty}×</span>
                    {m.card && <InkDot ink={m.card.ink_color as InkColor} size="sm" />}
                    <span className="truncate flex-1 text-foreground">{m.card?.name || m.name}</span>
                    {m.reason && <span className="text-ink-ruby text-[9px] flex-shrink-0">{m.reason}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={importing || goodCount === 0}
            className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50"
          >
            {importing ? 'Importing...' : goodCount === 0 ? 'Nothing to import' : `Import ${goodCount} card${goodCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
